import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { BookmarkPlus, BookmarkCheck, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PreviewBanner } from "@/components/sci/PreviewBanner";
import { SciMetroLine } from "@/components/sci/SciMetroLine";
import { SubjectFitCard, CourseFitCard, UniversityFitCard, ProfileLink } from "@/components/sci/FitCards";
import { Section, ClaimList, ModuleGroups } from "@/components/sci/Sections";
import { OutcomeTile } from "@/components/sci/OutcomeTile";
import { useSciPreview, shortName } from "@/components/sci/useSciPreview";
import { useShortlistWriter } from "@/lib/useShortlistWriter";
import { SciNotFound as NotFound, SciLoadError } from "@/components/sci/SciNotFound";

const SIBLING_LABEL = { oxford_ppe: "Oxford", edinburgh_phil: "Edinburgh", exeter_phil: "Exeter", berkeley_phil: "UC Berkeley" };

function ShortlistButton({ course, initial }) {
  const [on, setOn] = useState(initial);
  const { addToShortlist } = useShortlistWriter();
  const add = async () => {
    try {
      await addToShortlist({ university_id: course.university.id, degree_id: course.degree_id });
      setOn(true);
      toast.success("Added to your shortlist");
    } catch {
      toast.error("Couldn't add to your shortlist");
    }
  };
  return on ? (
    <Link to="/shortlist" data-testid="sci-course-shortlisted" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-emerald-700 px-5 text-sm font-medium text-emerald-800">
      <BookmarkCheck className="h-4 w-4" /> On your shortlist
    </Link>
  ) : (
    <Button onClick={add} data-testid="sci-course-shortlist-add" className="min-h-[44px] rounded-full bg-[#002147] px-5 hover:bg-[#0b3366]">
      <BookmarkPlus className="mr-2 h-4 w-4" /> Add to shortlist
    </Button>
  );
}

function Hero({ data }) {
  const c = data.course;
  const facts = [...data.sections.facts.filter((f) => f.field !== "fee"), ...data.sections.admissions.filter((f) => f.field === "entry_year")];
  return (
    <header className="py-10 lg:py-14">
      <Link to="/preview/sci/subjects/philosophy" data-testid="sci-course-back" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800">
        <ChevronLeft className="h-3.5 w-3.5" /> Philosophy
      </Link>
      <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-start gap-4">
          {c.university.logo_url && <img src={c.university.logo_url} alt="" className="h-14 w-14 shrink-0 rounded-lg border border-stone-200 bg-white object-contain p-1.5" />}
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#92400E]">{c.university.name}</p>
            <h1 className="mt-1 font-editorial text-4xl text-stone-900 sm:text-5xl" data-testid="sci-course-title">{c.title}</h1>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="sci-course-facts">
              {facts.map((f, i) => <span key={i} className="rounded-full bg-white px-3 py-1 font-mono text-xs text-stone-700 ring-1 ring-stone-200">{f.value}</span>)}
              <span className="rounded-full bg-violet-50 px-3 py-1 text-xs text-violet-800 ring-1 ring-violet-200">{c.philosophy_share}</span>
            </div>
          </div>
        </div>
        <ShortlistButton course={c} initial={data.fit.university.shortlisted} />
      </div>
    </header>
  );
}

function Outcomes({ items }) {
  const groups = items.reduce((a, o) => ((a[o.subject_tab || ""] = a[o.subject_tab || ""] || []).push(o), a), {});
  return (
    <div className="space-y-6">
      {Object.entries(groups).map(([tab, rows]) => (
        <div key={tab}>
          {tab && <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">{tab} graduates at this university (not PPE alone)</p>}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((o, i) => <OutcomeTile key={i} o={o} testId={`sci-outcome-${(tab || "course").toLowerCase()}-${i}`} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SciCoursePreview() {
  const { degreeId } = useParams();
  const { loading, data, missing, error, retry } = useSciPreview(`/sci/preview/courses/${degreeId}`);
  if (loading) return <div className="min-h-[60vh]" data-testid="sci-course-loading" />;
  if (missing) return <NotFound />;
  if (error) return <SciLoadError onRetry={retry} />;
  const s = data.sections;
  const metro = data.siblings.map((x) => ({ degree_id: x.degree_id, label: SIBLING_LABEL[x.key], sub: x.degree_id === degreeId ? "You are here" : "" }));
  return (
    <div className="bg-[#FDFBF7]" data-testid="sci-course-page">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>{`${data.course.title} · ${shortName(data.course)} · private preview`}</title></Helmet>
      <PreviewBanner runId={(data.run_ids || []).join(" + ")} />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Hero data={data} />
        <SciMetroLine subjectSlug="philosophy" courses={metro} current={degreeId} shortlisted={data.fit.university.shortlisted ? [degreeId] : []} />
        <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="sci-fit-triad">
          <SubjectFitCard fit={data.fit.subject} />
          <CourseFitCard fit={data.fit.course} />
          <UniversityFitCard fit={data.fit.university} uniId={data.course.university.id} />
        </div>
        <p className="mt-3 text-xs text-stone-500">Fit uses your saved profile only. <ProfileLink /></p>
        <Section id="admissions" kicker="Getting in" title="Admissions" empty={!s.admissions.length}><ClaimList items={s.admissions} prefix="sci-admissions" /></Section>
        <Section id="structure" kicker="The course" title="How it's organised" empty={!s.structure.length}><ClaimList items={s.structure} prefix="sci-structure" /></Section>
        <Section id="modules" kicker="The course" title="Modules and papers" empty={!s.compulsory_modules.length && !s.optional_modules.length}>
          {!!s.compulsory_modules.length && <><h3 className="mb-3 text-sm font-semibold text-stone-800">Compulsory</h3><ModuleGroups items={s.compulsory_modules} prefix="sci-module-compulsory" /></>}
          {!!s.optional_modules.length && <><h3 className="mb-3 mt-6 text-sm font-semibold text-stone-800">Options (examples published by the university)</h3><ModuleGroups items={s.optional_modules} prefix="sci-module-optional" /></>}
        </Section>
        <Section id="teaching" kicker="Learning" title="Teaching" empty={!s.teaching.length}><ClaimList items={s.teaching} prefix="sci-teaching" /></Section>
        <Section id="assessment" kicker="Learning" title="Assessment" empty={!s.assessment.length}><ClaimList items={s.assessment} prefix="sci-assessment" /></Section>
        <Section id="study-abroad" kicker="Options" title="Study abroad" empty={!s.study_abroad.length}><ClaimList items={s.study_abroad} prefix="sci-abroad" /></Section>
        <Section id="fees" kicker="Cost" title="Tuition fees" empty={!s.facts.some((f) => f.field === "fee")}><ClaimList items={s.facts.filter((f) => f.field === "fee")} prefix="sci-fee" /></Section>
        <Section id="careers" kicker="After graduating" title="Careers and employers" empty={!s.careers.length && !s.employers.length}>
          <ClaimList items={s.careers} prefix="sci-careers" />
          {!!s.employers.length && <><h3 className="mb-2 mt-6 text-sm font-semibold text-stone-800">Employers named by the university's careers service</h3><ModuleGroups items={s.employers} prefix="sci-employer" /></>}
        </Section>
        <Section id="outcomes" kicker="Official data" title="Graduate outcomes" empty={!s.outcomes.length}><Outcomes items={s.outcomes} /></Section>
        <Section id="hidden" kicker="Transparency" title="Not shown until verified">
          <ul className="space-y-2 text-sm text-stone-600" data-testid="sci-hidden-list">
            {data.hidden.map((h, i) => <li key={i} data-testid={`sci-hidden-${i}`}><span className="font-medium text-stone-800">{h.field}</span>: {h.reason}</li>)}
          </ul>
        </Section>
      </div>
    </div>
  );
}
